import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, clearSession, currentUser, currentRole, downloadFile } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, LogOut, FileDown, FileText } from "lucide-react";
import SignedAgreementCard from "./SignedAgreementCard";

const KPI = ({label, value, testid, color = "text-orange-600"}) => (
  <Card className="p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    <div className={`font-mono text-2xl font-bold mt-1 ${color}`}>{value}</div>
  </Card>
);

export default function FranchiseDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [comms, setComms] = useState([]);
  const [settlements, setSettlements] = useState([]);

  const load = async () => {
    if (currentRole() !== "franchise_admin") { nav("/franchise/login"); return; }
    try {
      const [dash, bk, wk, cm, st] = await Promise.all([
        api.get("/franchise/dashboard"),
        api.get("/franchise/bookings"),
        api.get("/franchise/workers"),
        api.get("/franchise/commissions"),
        api.get("/franchise/settlements"),
      ]);
      setD(dash.data); setBookings(bk.data); setWorkers(wk.data); setComms(cm.data); setSettlements(st.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) { clearSession(); nav("/franchise/login"); }
      else toast.error("Load failed");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  if (!d) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  const fr = d.franchise;
  const statusColor = fr.status === "active" ? "text-emerald-600 bg-emerald-50" : fr.status === "approved" ? "text-emerald-600 bg-emerald-50" : fr.status === "rejected" || fr.status === "suspended" ? "text-red-600 bg-red-50" : "text-amber-600 bg-amber-50";
  const feeColor = fr.fee_status === "paid" ? "text-emerald-600 bg-emerald-50" : "text-amber-600 bg-amber-50";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="franchise-dashboard">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center text-white"><Building2 size={18}/></div>
          <div>
            <div className="font-display font-extrabold text-slate-900">{fr.business_name}</div>
            <div className="text-xs text-slate-500 font-mono">{fr.code} · {fr.city}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {fr.fee_status === "paid" && (
            <Button size="sm" variant="outline" className="border-orange-300 text-orange-600"
              data-testid="fd-download-agreement"
              onClick={() => downloadFile("/franchise/agreement/pdf", `Karmivo_Franchise_Agreement_${fr.code}.pdf`).catch(() => toast.error("Download failed"))}>
              <FileText size={14} className="mr-1"/>Agreement
            </Button>
          )}
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${statusColor}`} data-testid="fd-status">{fr.status}</span>
          <span className={`px-2 py-0.5 rounded text-xs font-bold ${feeColor}`} data-testid="fd-fee-status">Fee: {fr.fee_status}</span>
          <Button variant="ghost" size="icon" onClick={() => { clearSession(); nav("/"); }} data-testid="fd-logout"><LogOut size={16}/></Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {fr.fee_status === "paid" && <SignedAgreementCard code={fr.code}/>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Today" value={d.today} testid="fd-kpi-today"/>
          <KPI label="Confirmed" value={d.confirmed} color="text-blue-600"/>
          <KPI label="In Progress" value={d.in_progress} color="text-orange-600"/>
          <KPI label="Completed" value={d.completed} color="text-emerald-600"/>
          <KPI label="Workers" value={d.workers_total}/>
          <KPI label="Available" value={d.workers_available} color="text-emerald-600"/>
          <KPI label="Gross Revenue ₹" value={d.gross_revenue?.toLocaleString() || 0} testid="fd-kpi-revenue"/>
          <KPI label="Franchise Net ₹" value={d.franchise_net?.toLocaleString() || 0} color="text-emerald-600" testid="fd-kpi-net"/>
        </div>

        <Tabs defaultValue="bookings">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="fd-tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="workers" data-testid="fd-tab-workers">Workers</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="fd-tab-commissions">Commissions</TabsTrigger>
            <TabsTrigger value="settlements" data-testid="fd-tab-settlements">Settlements</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-4 space-y-2">
            {bookings.length === 0 && <div className="text-sm text-slate-500">No bookings in your territory yet.</div>}
            {bookings.map(b => (
              <Card key={b.id} className="p-3 flex justify-between items-center text-sm">
                <div><div className="font-mono font-bold">{b.code}</div><div className="text-xs text-slate-500">{b.pricing?.service_label} · {b.scheduled_date}</div></div>
                <div className="text-right"><div className="font-mono font-bold text-emerald-600">₹{b.amount}</div><div className="text-xs text-slate-500">{b.assigned_count}/{b.worker_count} · {b.status}</div></div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="workers" className="mt-4 space-y-2">
            {workers.length === 0 && <div className="text-sm text-slate-500">No workers tagged to your franchise yet.</div>}
            {workers.map(w => (
              <Card key={w.id} className="p-3 flex justify-between items-center text-sm">
                <div><div className="font-bold">{w.name}</div><div className="text-xs text-slate-500">{w.category} · {w.phone}</div></div>
                <span className={`text-xs px-2 py-0.5 rounded font-bold ${w.kyc_status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{w.kyc_status}</span>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="commissions" className="mt-4 overflow-x-auto">
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-xs uppercase text-slate-600 font-bold"><tr><th className="p-3 text-left">Booking</th><th className="text-right">Gross</th><th className="text-right">Commission</th><th className="text-right">GST</th><th className="text-right">Net</th><th>Settled?</th></tr></thead>
                <tbody>
                  {comms.map(c => (
                    <tr key={c.id} className="border-t"><td className="p-3 font-mono text-xs">{c.booking_code}</td><td className="text-right">₹{c.gross_booking_value}</td><td className="text-right text-orange-600 font-bold">₹{c.commission}</td><td className="text-right">₹{c.commission_gst}</td><td className="text-right font-bold text-emerald-600">₹{c.franchise_net_payable}</td><td className="text-center">{c.settled ? "✓" : "—"}</td></tr>
                  ))}
                  {comms.length === 0 && <tr><td colSpan="6" className="p-4 text-center text-slate-500">No commissions yet.</td></tr>}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          <TabsContent value="settlements" className="mt-4 space-y-2">
            {settlements.length === 0 && <div className="text-sm text-slate-500">No settlements yet.</div>}
            {settlements.map(s => (
              <Card key={s.id} className="p-3 flex justify-between items-center text-sm" data-testid={`fd-settlement-${s.code}`}>
                <div><div className="font-mono font-bold">{s.code}</div><div className="text-xs text-slate-500">{s.commission_count} bookings · {s.frequency}</div></div>
                <div className="flex items-center gap-3">
                  <div className="text-right"><div className="font-mono text-emerald-600 font-bold">₹{s.net_payable}</div><div className="text-xs text-slate-500">{s.status}</div></div>
                  <Button size="sm" variant="outline" className="border-slate-300" data-testid={`fd-settlement-pdf-${s.code}`}
                    onClick={() => downloadFile(`/franchise/settlements/${s.id}/pdf`, `Karmivo_Settlement_${s.code}.pdf`).catch(() => toast.error("Download failed"))}>
                    <FileDown size={14} className="mr-1"/>Statement
                  </Button>
                </div>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
